import { Alert, CircularProgress, Typography } from '@mui/material';
import { FlexContainer } from '@act/storybook';
import { useCallback, useState } from 'react';
import { generateDatadogTrackingProps } from '@act/toolkit/environment/environment/userMonitoring.util';
import { usePlacesAutocomplete } from './hooks/usePlacesAutocomplete.hook';
import { useResolvePlaceWithCache } from './hooks/useResolvePlaceWithCache.hook';
import type { CboResourceFormValues } from '../forms/cboResourceForm/cboResourceForm.schema';

export interface PlacesSearchProps {
  /** Label above the search field. */
  label?: string;
  /** When false, only the autocomplete input is shown (e.g. resource-call browse step). */
  showLabel?: boolean;
  /** Placeholder for the autocomplete input. */
  placeholder?: string;
  /** Member address used to softly rank Places predictions by proximity. */
  locationBiasAddress?: string;
  /** Called with partial form values after cache hit or Google Details fetch. */
  onFormAutofill?: (values: Partial<CboResourceFormValues>) => void;
  /** When false, hides the post-selection summary line under the input. */
  showSelectionSummary?: boolean;
}

/**
 * Reusable Google Places predictive search with server cache before Place Details API.
 *
 * @param props - Labels and autofill callback.
 * @returns Autocomplete host UI and status messaging.
 */
export function PlacesSearch({
  label = 'Find a local business',
  showLabel = true,
  placeholder = 'Search for resource',
  locationBiasAddress,
  onFormAutofill,
  showSelectionSummary = true,
}: PlacesSearchProps) {
  const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(
    null
  );
  const [selectionSummary, setSelectionSummary] = useState<string | null>(null);
  const { resolveFromGmpSelect } = useResolvePlaceWithCache();

  const handleGmpSelect = useCallback(
    async (event: Parameters<typeof resolveFromGmpSelect>[0]) => {
      const partial = await resolveFromGmpSelect(event);
      onFormAutofill?.(partial);

      const summaryName = partial.name ?? '';
      const summaryAddress =
        partial.formattedAddress ?? partial.address ?? '';
      const summary =
        summaryName && summaryAddress
          ? `${summaryName} — ${summaryAddress}`
          : summaryAddress || summaryName;

      setSelectionSummary(summary || null);
    },
    [onFormAutofill, resolveFromGmpSelect]
  );

  const { isReady, loadError, widgetUnavailable } = usePlacesAutocomplete({
    container: containerElement,
    locationBiasAddress,
    placeholder,
    onGmpSelect: handleGmpSelect,
  });

  return (
    <FlexContainer direction="column" gap={2} width="100%" overflowAllVisible>
      {showLabel ? (
        <Typography variant="labelLarge" color="text.secondary">
          {label}
        </Typography>
      ) : null}
      {loadError ? (
        <Alert severity="error">
          Could not load Google Maps. Try refreshing the page.
        </Alert>
      ) : null}
      {widgetUnavailable ? (
        <Alert severity="warning">
          Place search is unavailable. Enable <strong>Places API (New)</strong> for your
          Google Maps key, then reload.
        </Alert>
      ) : null}
      {!isReady && !loadError && !widgetUnavailable ? (
        <CircularProgress size={24} />
      ) : null}
      <div
        {...generateDatadogTrackingProps('places-search-host')}
        ref={setContainerElement}
      />
      {showSelectionSummary && selectionSummary ? (
        <Typography variant="bodySmall" color="text.secondary">
          Selected: {selectionSummary}
        </Typography>
      ) : null}
    </FlexContainer>
  );
}
